<template>
  <ActionMenu
    class="doc-lists-menu"
    :position="position"
    :iconSize="iconSize"
    :class="{ 'doc-lists-menu--marked': view.isDocInAnyList }"
    :icon="view.isDocInAnyList ? 'iflag2' : 'iflag'"
    v-bind="$attrs"
  >
    <template v-if="useIconButton" #button>
      <IconButton
        :icon="view.isDocInAnyList ? 'iflag2' : 'iflag'"
        tooltip="Add / Remove from lists"
        outlined
      />
    </template>

    <Action
      class="doc-lists-menu--favorite"
      :icon="doc.user.is_favorite ? 'ifavorite-remove' : 'icontrol-favorite'"
      @click.prevent="toggleFavorite()"
    >
      {{ doc.user.is_favorite ? 'Remove from Favorites' : 'Add to Favorites' }}
    </Action>
    <Action
      v-if="doc.doc_type === 'book'"
      class="doc-lists-menu--want-to-read"
      :icon="view.isInWantToRead ? 'iflag-remove' : 'iflag'"
      @click.prevent="view.toggleWantToRead()"
    >
      {{
        view.isInWantToRead ? 'Remove from Want to Read' : 'Add to Want to Read'
      }}
    </Action>
    <Action
      v-if="doc.doc_type === 'book'"
      class="doc-lists-menu--add-to-list"
      icon="iplayer-fast"
      @click.prevent="addToListPopup?.show"
    >
      Add to List
    </Action>
  </ActionMenu>

  <Teleport to="#appRoot">
    <AddToListPopup ref="addToListPopup" :view="view" />
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { Raw } from 'vue'
import { useWait } from '@/app/composables/useWait'

import type { UserDocListItem } from '@/models/interfaces'
import { toggleDocFavorite } from '@/models/doc.favorite'
import { DocCustomListsView } from '@/models/doc.custom.lists'
import type { DocsView } from '@/models/doc'

import ActionMenu from '@/app/components/ActionMenu.vue'
import Action from '@/app/components/ActionMenuItem.vue'
import AddToListPopup from './AddToListPopup.vue'
import IconButton from '@/app/components/button/IconButton.vue'

/**
 * This component is a wrapper on top of `ActionMenu` with
 * actions for adding the doc to Favorites and moving it
 * between Custom Lists.
 *
 * @param doc - The doc upon which these actions will take effect.
 * @param docsView - For updating the list when adding / removing docs from lists.
 * @param position - The dropdown menu position.
 * @param iconSize - The font size of the button.
 * @param useIconButton - Whether or not to use the IconButton component for the toggle button.
 */
const props = withDefaults(
  defineProps<{
    doc: UserDocListItem
    docsView?: DocsView
    position?: 'center' | 'right'
    iconSize?: 'normal' | 'small'
    useIconButton?: boolean
  }>(),
  {
    position: 'right',
    iconSize: 'normal',
    useIconButton: false,
  },
)

const wait = useWait()
const addToListPopup = ref<InstanceType<typeof AddToListPopup> | null>(null)

const view = ref<Raw<DocCustomListsView>>(
  new DocCustomListsView(props.doc, wait, props.docsView),
)

async function toggleFavorite(): Promise<void> {
  toggleDocFavorite(props.doc, props.docsView)
}
</script>

<style lang="scss" scoped>
.doc-lists-menu {
  animation: fadeIn 0.3s;
  --button-color: #{$c-black-9};
  --button-background-hover: #{rgba($c-black-2, 0.05)};
}

.doc-lists-menu--marked {
  --button-color: #{$c-yellow};
}

.night-mode .doc-lists-menu {
  --button-background-hover: #{rgba($c-dark-3, 0.5)};
  --button-color: #{$c-border-7};
}

.night-mode .doc-lists-menu--marked {
  --button-color: #{$c-yellow};
}
</style>
